/**
 * Backend HTTP API types shared between backend, web-student, web-admin, agent.
 */

import type { EncryptionMode } from "./e2ee.js";
import type { MediaProfile } from "./admin.js";

export type { EncryptionMode } from "./e2ee.js";
export type { MediaProfile } from "./admin.js";

/** UI locale code. Lowercase BCP-47-ish; only these two are supported. */
export type Locale = "de" | "en";

/** RBAC role union. Source of truth is the `user_roles` table. */
export type Role = "admin" | "authority" | "school-manager" | "teacher" | "student";

/**
 * One resolved role assignment for a user. `authority_id`/`school_id` are
 * scoped per the role invariants (admin: both null; authority: authority_id set;
 * school-manager/teacher/student: school_id set). Names are best-effort joins.
 */
export interface UserRole {
  id: number;
  role: Role;
  authority_id: number | null;
  school_id: number | null;
  authority_name?: string | null;
  school_name?: string | null;
}

export interface LoginRequest {
  /** Login identifier. Email (case-insensitive); lowercased server-side. */
  email: string;
  password: string;
  /**
   * IDs of active sessions the student chose to kill to free a concurrent-
   * session slot, sent on a `session_limit` (409) retry. The backend re-
   * verifies the password before killing them. Omit on the first attempt.
   */
  kill_session_ids?: string[];
  /**
   * Opt-in "stay signed in": issue a long-lived, IDLE-SLIDING session (7 days
   * of inactivity) instead of the default 8h one. The session still dies at a
   * hard 90-day absolute cap counted from login. Omitted/false ⇒ default TTL.
   * IGNORED for the shared demo account (its short TTL always wins).
   */
  remember?: boolean;
}

export interface LoginResponse {
  ok: true;
  email: string;
  /**
   * The user's NICKNAME — a short, friendly display name shown across the
   * student UI INSTEAD OF the email (header, viewer strip). Historically named
   * `display_name`; the profile page lets the student edit it. `null` ⇒ the UI
   * falls back to the email.
   */
  display_name: string | null;
  /**
   * The user's full/real name (e.g. "Maria Musterfrau"). Distinct from the
   * nickname (`display_name`). Optional/`null` when unset. Absent on older
   * backends ⇒ treat as null.
   */
  full_name?: string | null;
  roles: UserRole[];
  /**
   * The user's preferred UI language. The student app uses this to pick its locale
   * at login. Defaults to "de" server-side when unset.
   */
  language: Locale;
  /**
   * Whether the user already has a profile image set. The student app uses this
   * to decide whether to prompt for a photo on login. The image itself is never
   * inlined here — fetch it via `GET /me/profile-image` to keep the payload small.
   */
  has_profile_image: boolean;
  /**
   * True when this is the guarded demo principal (the seeded demo student
   * account, only present when `demo.enabled` is on). The student app shows a
   * persistent "Test-Modus" badge + a placeholder stream while this is set.
   * Absent on older backends ⇒ treat as false.
   */
  demo?: boolean;
  /**
   * True when the user must change their password before continuing (admin
   * created them with an initial password). Frontends should force the change.
   * Absent on older backends ⇒ treat as false.
   */
  must_change_password?: boolean;
}

export interface MeResponse {
  ok: true;
  email: string;
  /** Nickname shown instead of the email (see {@link LoginResponse.display_name}). */
  display_name: string | null;
  /** Full/real name (see {@link LoginResponse.full_name}). */
  full_name?: string | null;
  roles: UserRole[];
  language: Locale;
  /** Whether a profile image is set (see {@link LoginResponse.has_profile_image}). */
  has_profile_image: boolean;
  /**
   * E2EE scaffolding flag (Phase 5). Global server config; absent on older
   * backends ⇒ treat as false. When false the E2EE feature is entirely inert.
   */
  e2ee_enabled?: boolean;
  /**
   * The email address the user has requested to change to but not yet confirmed
   * (a verification link was sent to it). `null`/absent when no change is
   * pending. Drives the "verification sent to X" pending-state UI on the
   * profile page.
   */
  pending_email?: string | null;
  /**
   * True when the user must change their password (see
   * {@link LoginResponse.must_change_password}). Absent ⇒ treat as false.
   */
  must_change_password?: boolean;
}

// ----- Self-service account management (profile page) -----

/**
 * PATCH /me — update the current user's own editable profile fields. All fields
 * are optional (partial update); omitted fields are left unchanged. A field set
 * to `null` clears it (where nullable). Requires a valid session cookie.
 */
export interface UpdateProfileRequest {
  /** New nickname (shown instead of email). `null` clears it. */
  display_name?: string | null;
  /** New full/real name. `null` clears it. */
  full_name?: string | null;
  /** New preferred UI language. */
  language?: Locale;
}

/** PATCH /me response — the updated, canonical profile values. */
export interface UpdateProfileResponse {
  ok: true;
  display_name: string | null;
  full_name: string | null;
  language: Locale;
}

/**
 * POST /me/change-password — change the current user's password. The current
 * password is REQUIRED (re-authentication) so a hijacked session cannot silently
 * change it. On success all OTHER sessions are invalidated.
 */
export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
}

/**
 * POST /me/change-email — request changing the current user's login email. The
 * current password is REQUIRED. The change is NOT applied immediately: a
 * verification link is sent to the NEW address and the swap only happens once
 * that link is confirmed (`GET /me/change-email/confirm/:token`). Until then the
 * old email keeps working and `MeResponse.pending_email` reflects the target.
 */
export interface ChangeEmailRequest {
  new_email: string;
  current_password: string;
}

/** GET /me/pending-email — the currently pending (unconfirmed) email change. */
export interface PendingEmailResponse {
  ok: true;
  /** Target address awaiting confirmation, or null when none is pending. */
  pending_email: string | null;
}

/**
 * Well-known error codes for the self-service account endpoints, returned in
 * `ErrorResponse.error`. Frontends switch on these to localize the message.
 */
export type AccountErrorCode =
  | "bad_request"
  | "wrong_password"
  | "email_taken"
  | "weak_password"
  | "invalid_email";

/** One teacher contact (name + email) for a room. */
export interface TeacherContact {
  /** Teacher's display name, or null when unset (UI falls back to email). */
  name: string | null;
  /** Teacher's email — the room's contact address. */
  email: string;
}

/**
 * GET /me/teacher-contact?room=<name> — the contact address(es) for a room the
 * student belongs to (the assigned teacher's email). Read-only. `room` is the
 * room name (e.g. the one the student is currently connected to). Returns an
 * empty `teachers` array when the room has no assigned teacher.
 */
export interface TeacherContactResponse {
  ok: true;
  /** The resolved room name, or null when no room matched. */
  room: string | null;
  teachers: TeacherContact[];
}

// ----- Profile image (student avatar) -----

/**
 * Hard cap on the stored profile-image data URL, in bytes. Clients downscale a
 * photo to a small square (~256x256) JPEG before upload; the server rejects any
 * payload whose data-URL string exceeds this with HTTP 413. ~256 KiB leaves
 * generous headroom for a small square JPEG while keeping the DB row modest.
 */
export const PROFILE_IMAGE_MAX_BYTES = 256 * 1024;

/**
 * Hard cap on the stored FULL-resolution profile-image data URL, in bytes. The
 * full rendition is a high-res (up to 1920x1080, 16:9) JPEG used by the kiosk as
 * a video-tile placeholder; clients adapt JPEG quality to keep the data-URL
 * string under this. Payloads exceeding this are rejected with HTTP 413. ~2 MiB
 * leaves room for a 1080p JPEG while bounding the DB row.
 */
export const PROFILE_IMAGE_FULL_MAX_BYTES = 2 * 1024 * 1024;

/** POST /me/profile-image — set the current user's avatar. */
export interface SetProfileImageRequest {
  /**
   * A `data:image/...;base64,...` URL. Must be <= {@link PROFILE_IMAGE_MAX_BYTES}
   * bytes (the whole string). `null` clears the image.
   */
  image: string | null;
  /**
   * Optional full-resolution rendition as a `data:image/...;base64,...` URL.
   * Must be <= {@link PROFILE_IMAGE_FULL_MAX_BYTES} bytes (the whole string).
   * Semantics: omitted/undefined ⇒ leave the existing full unchanged; `null` ⇒
   * clear the full rendition only; a string ⇒ set the full rendition. When the
   * thumbnail `image` is `null`, BOTH renditions are cleared regardless of this.
   */
  imageFull?: string | null;
}

/**
 * GET /me/profile-image — the current user's avatar (null when unset).
 * The `?size=full|thumb` query param selects which rendition is returned in
 * `image` (default "thumb"); the response shape is the same for both.
 */
export interface ProfileImageResponse {
  ok: true;
  /**
   * Root-relative public URL path to the image (e.g. `/profile-images/<hash>.jpg`),
   * or null when the user has no image. The client resolves it against the
   * backend base. This is NOT a data URL (image bytes are served from disk).
   */
  image: string | null;
}

export interface ErrorResponse {
  ok: false;
  error: string;
}

/**
 * Well-known auth/account error codes returned in `ErrorResponse.error`.
 * Frontends switch on these (e.g. `email_not_verified` → offer "resend").
 */
export type AuthErrorCode =
  | "bad_request"
  | "invalid_credentials"
  | "email_not_verified"
  | "invalid_or_expired_token";

// ----- Account lifecycle: email verification + password reset -----

/** POST /auth/request-verify — resend a verification link. */
export interface RequestVerifyRequest {
  /** Target email. Optional when the caller is authenticated (uses own account). */
  email?: string;
}

/** POST /auth/request-reset — request a password-reset link. Always 200. */
export interface RequestResetRequest {
  email: string;
}

/** POST /auth/reset/:token — set a new password from a reset token. */
export interface ResetPasswordRequest {
  password: string;
}

/**
 * Generic success envelope used by the account lifecycle endpoints
 * (request-verify, verify, request-reset, reset). Always `{ ok: true }` on
 * success; failures use {@link ErrorResponse}.
 */
export interface OkResponse {
  ok: true;
}

export interface TokenRequest {
  /**
   * Target room name. Optional: the device principal ignores it (room is
   * resolved from the device→room binding), but student/admin sessions still send
   * it to pick the room they want to join.
   */
  room?: string;
  /**
   * E2EE capability of the requesting client, from the runtime
   * `isE2EESupported()` (livekit-client). The SOURCE OF TRUTH for whether this
   * client can do insertable-streams E2EE — the backend uses it (with the bound
   * device's `force_encryption`) to negotiate the whole-session E2EE state and
   * to BLOCK a join when a forcing device meets an incapable client. Omitted by
   * older clients / the device principal ⇒ treated as `false` (no E2EE).
   * Only consulted when `E2EE_ENABLED` is on.
   */
  e2ee_supported?: boolean;
  /**
   * The student's persisted opt-out preference (plaintext when allowed). Only
   * honoured when the bound device does NOT force encryption. Omitted ⇒ false
   * (default "E2EE on if available"). Only consulted when `E2EE_ENABLED` is on.
   */
  e2ee_opt_out?: boolean;
}

/**
 * Negotiated whole-session E2EE state returned to a STUDENT in the token
 * response (present only when `E2EE_ENABLED` is on). The student app drives the
 * overlay lock icon + opt-out toggle from this:
 *  - `active`: per-frame E2EE is ON for the session.
 *  - `forced`: the bound device forces E2EE; the student CANNOT opt out (the
 *    overlay toggle is rendered locked/disabled).
 */
export interface TokenE2eeSession {
  active: boolean;
  forced: boolean;
  /** Stable diagnostic reason (e.g. "forced", "opt_out", "unsupported"). */
  reason?: string;
  /**
   * Phase 6: the bound device's CURRENT active media key_id (content-addressed),
   * or null when no device has registered one. The student compares this against
   * the key_id of the key it actually holds to detect a rotation — when they
   * differ (or the student holds none) while `active` is true, the overlay shows
   * a "re-scan QR" prompt. Non-secret; the key MATERIAL never travels here.
   */
  key_id?: string | null;
  /**
   * Phase 2: the whole-room NEGOTIATED encryption mode at this token's issuance.
   * A NON-forced room downgrades to `"plaintext"` when any live participant
   * cannot decrypt (unsupported browser, or capable-but-keyless after the
   * auto-seal heal window); otherwise `"encrypted"`. A forced room is ALWAYS
   * `"encrypted"`. In `"plaintext"` mode a capable student MUST NOT enable
   * per-frame decryption (the publisher is sending plaintext). Optional/omitted
   * when the global E2EE flag is off ⇒ treat as `"encrypted"` (pre-Phase-2
   * behaviour: encrypt when `active`).
   */
  encryption_mode?: EncryptionMode;
}

/**
 * Structured non-token error returned (HTTP 403) when a join is BLOCKED by the
 * E2EE policy — the bound device forces encryption but the requesting client
 * cannot do insertable streams. `code` is stable + machine-readable so the
 * frontend maps it to localized DE/EN copy; the English `title`/`detail`/`steps`
 * are an always-present fallback.
 */
export interface E2eeRequiredResponse {
  ok: false;
  error: "e2ee_required";
  remediation: {
    /** Stable code the frontend maps to localized copy. */
    code: string;
    /** English fallback title. */
    title: string;
    /** English fallback detail. */
    detail: string;
    /** English fallback ordered remediation steps. */
    steps: string[];
  };
}

/**
 * GET /device/ping response — liveness/auth probe for the kiosk SPA. Historically
 * just `{ ok: true }`; Phase 1.2 + Phase 2 add side-effect-free E2EE fields so
 * the kiosk learns mode changes + reseal requests on its existing ping loop.
 * The probe itself stays side-effect-free EXCEPT it drains (clears) the per-room
 * `reseal_now` marker when reporting it.
 */
export interface DevicePingResponse {
  ok: true;
  /**
   * Phase 2: the whole-room NEGOTIATED encryption mode for the device's room.
   * The kiosk polls this and reconnects its publisher when it changes. Present
   * only when the global E2EE flag is on; omitted ⇒ treat as `"encrypted"`.
   */
  encryption_mode?: EncryptionMode;
  /**
   * Phase 1.2: true when a student token was issued to a student who lacks the
   * room's current `key_id`, so the kiosk should immediately re-seal/distribute
   * the media key (faster than the 60s periodic timer). Server marker only; it
   * is CLEARED when read here, so it reports `true` at most once per request.
   * Present only when the global E2EE flag is on; omitted/false ⇒ no action.
   */
  reseal_now?: boolean;
}

/**
 * POST /livekit/e2ee/keyless request (student auth). A connected, CAPABLE
 * student that still holds no usable media key after its auto-seal heal/retry
 * window reports the anomaly here. The server marks that student `keyless=true`
 * in the room's presence map (triggering a non-forced downgrade) AND writes a
 * loud `e2ee_autoseal_failed` audit. Inert (404) when the E2EE flag is off.
 */
export interface E2eeKeylessRequest {
  /** Room name the student is connected to. */
  room: string;
  /**
   * The room's current active media `key_id` the student expected to receive
   * (from its negotiated session state), for the audit trail. Optional.
   */
  expected_key_id?: string | null;
}

/** POST /livekit/e2ee/keyless response. */
export interface E2eeKeylessResponse {
  ok: true;
}

// ----- Rooms (student-facing) -----

export type RoomVisibility = "public" | "restricted";

export interface RoomSummary {
  name: string;
  visibility: RoomVisibility;
  online: boolean;
  /**
   * True when this room is the guarded demo room (only present while
   * `demo.enabled` is on). The kid app renders a placeholder/dummy stream for
   * it when no real classroom publisher is present. Absent ⇒ treat as false.
   */
  demo?: boolean;
  /**
   * Wall-clock ms of the bound device's most recent heartbeat, or null when the
   * room has no device or the device has never beaten. Lets the student app show
   * "last online at <time>" and apply its own offline timeout.
   */
  lastSeen: number | null;
  /**
   * Wall-clock ms when the device's most recent continuous online streak began,
   * or null when unknown. `lastSeen - onlineSince` ≈ how long it was online the
   * last time.
   */
  onlineSince: number | null;
}

export interface RoomListResponse {
  ok: true;
  rooms: RoomSummary[];
}

/**
 * WebRTC ICE server entry handed to clients so they can reach the SFU through
 * the self-hosted coturn relay (TURN/TLS on 443) when their network blocks
 * LiveKit's direct UDP/ICE-TCP candidates — e.g. locked-down school firewalls.
 * Structurally compatible with the browser's `RTCIceServer`. Defined locally so
 * `shared` does not depend on the DOM lib (it is also imported by the agent).
 */
export interface IceServer {
  urls: string | string[];
  username?: string;
  credential?: string;
}

/**
 * One enabled classroom camera, surfaced to the kiosk (to publish) and — via
 * its `name` carried as the LiveKit track name — to the student (to label the
 * camera switcher). Multiple cameras may be enabled simultaneously; the kiosk
 * publishes one LiveKit video track per entry.
 */
export interface TokenCamera {
  /** Linux device node path, e.g. `/dev/video2`. Stable id across the system. */
  path: string;
  /**
   * Operator-assigned display name (from `DeviceConfig.cameras[].name`). Used as
   * the published LiveKit track name so the student can show it in the switcher.
   */
  name: string;
  /**
   * Resolved V4L2 card label for browser `MediaDeviceInfo.label` matching, or
   * null when no enumeration label is available (kiosk falls back to default).
   */
  label: string | null;
}

export interface TokenResponse {
  ok: true;
  token: string;
  url: string; // wss URL of LiveKit
  identity: string;
  /**
   * Ephemeral TURN/STUN servers for the WebRTC PeerConnection. Omitted when the
   * backend has no coturn secret configured (clients then use LiveKit defaults.
   */
  iceServers?: IceServer[];
  /**
   * Operator-selected camera label (V4L2 card name) for the kiosk to bind.
   * Bridges between the Linux `/dev/video*` device the admin picked and the
   * browser's opaque `MediaDeviceInfo.deviceId`: the kiosk SPA matches against
   * `MediaDeviceInfo.label` (substring, case-insensitive) and passes the
   * resolved `deviceId` into the LiveKit capture constraints.
   *
   * Empty / undefined when `cfg.camera == "auto"`, when no enumeration has
   * been received yet, or when the configured path is no longer present in
   * the latest enumeration. Clients then fall back to the browser default.
   *
   * @deprecated Single-camera back-compat. Prefer `cameras` (multi-camera).
   * Equals the first enabled camera's label.
   */
  camera?: string;
  /**
   * All enabled classroom cameras for the device principal to publish
   * simultaneously (one LiveKit video track each, named by `TokenCamera.name`).
   * Empty / undefined for student/admin sessions, or when no camera is enabled.
   * Supersedes the single `camera` field.
   */
  cameras?: TokenCamera[];
  /** Resolved media profile for the kiosk; omitted to use hardcoded fallback. */
  media_profile?: MediaProfile;
  /**
   * Operator-selected microphone label (audio device NAME) for the kiosk to
   * bind. Same bridging rationale as `camera`: the admin picks a device by
   * name, the agent enumerates audio devices, and the kiosk SPA substring-
   * matches against `MediaDeviceInfo.label` (case-insensitive) to resolve the
   * opaque `deviceId` it passes into the LiveKit capture constraints.
   *
   * Empty / undefined when `cfg.mic == "auto"`, when no enumeration has been
   * received yet, or when the configured name is no longer present (with an
   * input channel) in the latest enumeration. Clients fall back to the
   * browser default.
   */
  mic?: string;
  /**
   * Operator-selected speaker label (audio device NAME) for the kiosk to route
   * remote (student) audio to via `HTMLMediaElement.setSinkId`. Same bridging
   * rationale as `camera` / `mic`.
   *
   * Empty / undefined when `cfg.speaker == "auto"`, when no enumeration has
   * been received yet, or when the configured name is no longer present (with
   * an output channel) in the latest enumeration. Clients fall back to the
   * browser default output.
   */
  speaker?: string;
  /**
   * Classroom speaker output volume in [0, 1]. Applied by the kiosk as
   * `HTMLMediaElement.volume` on the remote-audio element. Only surfaced for
   * the device principal; omitted on student/admin sessions. When omitted the
   * client should treat the value as 1 (default 100%).
   */
  volume?: number;
  /**
   * operator-set OS master volume [0,100], delivered to the device principal
   */
  masterVolume?: number;
  /**
   * E2EE scaffolding (Phase 5). Present ONLY for the device principal AND ONLY
   * when the global `E2EE_ENABLED` flag is on. Lets the kiosk render its QR +
   * force-encryption affordance. Omitted entirely when the flag is off (so the
   * kiosk behaves exactly as before). Carries NO secret key material — only the
   * key id + force flag. The symmetric key material for the QR lives
   * device/client-side (placeholder in scaffolding).
   */
  e2ee?: {
    enabled: true;
    force_encryption: boolean;
    e2ee_key_id: string | null;
    /**
     * Phase 2: the whole-room NEGOTIATED encryption mode for the kiosk's room at
     * this token's issuance. The kiosk publishes encrypted vs plaintext per this
     * value; when it later changes (learned via the `devicePing` loop) the kiosk
     * reconnects its publisher in the new mode. A forced room is always
     * `"encrypted"`. Omitted on older backends ⇒ treat as `"encrypted"`.
     */
    encryption_mode?: EncryptionMode;
  };
  /**
   * Negotiated whole-session E2EE state for the STUDENT principal (present only
   * when `E2EE_ENABLED` is on AND the principal is a student). Drives the
   * student overlay lock icon + opt-out toggle. Omitted for the device principal
   * (which uses `e2ee` above) and when the flag is off.
   */
  e2ee_session?: TokenE2eeSession;
  /**
   * True when invisible mode is FORBIDDEN for this joining student, because the
   * authority, school, room, OR any device bound to the room has the
   * `forbid_invisible` setting on. The student app then hides the "go invisible"
   * control and forces VISIBLE presence immediately on connect. Present only for
   * the student principal; absent ⇒ treat as false (invisible allowed).
   */
  invisible_forbidden?: boolean;
  /**
   * True when QR key exchange is FORCED for this principal's room, because the
   * authority, school, room, OR any device bound to the room has the
   * `force_e2ee_qr` setting on. Auto-seal key distribution is disabled — keys
   * must be exchanged in person via QR. Present for BOTH the device principal
   * (kiosk skips auto-seal, surfaces the QR) and the student principal (shows
   * "scan required" instead of waiting for silent auto-load). Absent ⇒ treat as
   * false (auto-seal allowed).
   */
  force_e2ee_qr?: boolean;
  /**
   * WHICH hierarchy level forced the QR key exchange, for the kiosk to display
   * (e.g. "QR required by your school"). The most-specific level whose
   * `force_e2ee_qr` flag is set, checked room → school → authority → device.
   * Present only for the DEVICE principal and only when `force_e2ee_qr` is true.
   * Absent ⇒ source unknown / not forced.
   */
  force_e2ee_qr_source?: "room" | "school" | "authority" | "device";
  /**
   * The resolved E2EE media-key rotation hour (0-23 local) for this device's
   * room. MOST-SPECIFIC wins: device → room → school → authority → default 0.
   * Present only for the DEVICE principal; always included for the device (0
   * when nothing in the hierarchy sets it). Absent ⇒ treat as 0.
   */
  e2ee_rotate_hour?: number;
  /**
   * True when end-to-end encryption is REQUIRED for this principal's room,
   * because the authority, school, room, OR any device bound to the room has
   * the `force_e2ee` setting on (OR-ed with `force_e2ee_qr`, which implies it).
   * Encryption is mandatory (no opt-out, unsupported viewers blocked), but
   * server-mediated auto-seal key distribution stays ENABLED — unlike
   * `force_e2ee_qr`. Present for both principals; absent ⇒ treat as false.
   */
  force_e2ee?: boolean;
  /**
   * True when the authenticated principal is a demo principal (the seeded demo
   * student account OR a demo device token). The kiosk/device app shows a
   * persistent "Test-Modus" badge while this is set. Absent ⇒ treat as false.
   */
  demo?: boolean;
}

export interface HealthResponse {
  ok: true;
  service: "backend";
  version: string;
  uptime_s: number;
}

// ----- Demo (public, gated by the `demo.enabled` admin setting) -----

/**
 * Response of `POST /demo/token` — a freshly minted 3-day demo DEVICE token
 * bound to the demo room. Returned only when `demo.enabled` is on (else 403).
 * The website displays the token + the expiry/countdown; the token authenticates
 * as the `device` principal (Test-Modus) for a temporary self-hosted demo.
 */
export interface DemoTokenResponse {
  ok: true;
  /** The demo device bearer token (secret). */
  token: string;
  /** Epoch milliseconds when the token expires (now + 3 days). */
  expiresAt: number;
  /** Name of the demo room the token is bound to. */
  room: string;
}

/**
 * Body of `POST /demo/token`. Carries the tester's email (required) plus the
 * Altcha solution + honeypot fields, mirroring the contact form
 * (`altcha` = solution payload, `hp` = honeypot, must stay empty). The backend
 * emails the minted token (and a set-password link for the auto-created student
 * account) to `email`.
 */
export interface DemoTokenRequest {
  /** Tester's email (required). Receives the demo token + set-password link. */
  email: string;
  /** Altcha PoW solution payload (same field name as the contact form). */
  altcha?: string;
  /** Honeypot: must be empty; a non-empty value flags a bot. */
  hp?: string;
}

// ----- Public runtime config (unauthenticated) -----

/**
 * Response of `GET /public-config`. Unauthenticated runtime flags plus the
 * network-diagnostic probe targets the device agent needs to know WHAT to
 * reach before it holds a token (link → DNS → backend → LiveKit → TURN).
 *
 * SECURITY: `turnUrls` carries HOSTS/PORTS/TRANSPORT ONLY — never a TURN
 * username or credential. Those are minted per-session in the LiveKit token
 * response (`IceServer`), never here. The bare hostnames are already visible
 * to anyone holding a minted token, so publishing them adds no new secret,
 * but credential-looking URL components are stripped defensively server-side.
 *
 * Both target fields are OPTIONAL: when the deployment has not configured a
 * value the field is `null` and the agent treats those probes as
 * "targets unknown" (NET_TARGETS_UNKNOWN) and skips them rather than failing.
 */
export interface PublicConfigResponse {
  ok: true;
  /** Beta gate for the public website. Always present (existing consumers). */
  beta: boolean;
  /** Public LiveKit signalling WebSocket URL, or null when unset. */
  livekitWsUrl?: string | null;
  /** TURN/STUN URIs, credential-free (`turns:host:443?transport=tcp`). */
  turnUrls?: string[] | null;
}
